import styled, { css, keyframes } from 'styled-components';

import { ANIM, COLORS, MOBILE, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';
import { hoverWash } from '../../../../theme/mixins';
import { Text } from '../../../../components';
import { ProviderIcon } from '../../../social-login/components/ProviderIcon/ProviderIcon';

/** The panel heading: Google's own "G" in front of the SectionTitle, laid out like SectionTitle's icon + title. */
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  /* SectionTitle's own icon gap and bottom margin (off the SPACE scale), so this heading matches the panel above. */
  gap: 10px;
  margin-bottom: 18px;

  > :last-child {
    margin-bottom: 0;
  }
`;

/** Google's multi-colour "G" (the sign-in button's mark), at SectionTitle's 20px icon size. */
export const GoogleMark = styled(ProviderIcon)`
  flex: 0 0 auto;
  font-size: 20px;
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

/** The numbered how-to. An ordered list whose numbers come from a CSS counter, so no digit is rendered as copy. */
export const Steps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: sc-step;
`;

export const Step = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[12]};
  counter-increment: sc-step;

  &::before {
    content: counter(sc-step);
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 99px;
    border: 1px solid ${COLORS.hairline};
    background: ${COLORS.surfaceCard};
    font-size: 12px;
    font-weight: 700;
    color: ${COLORS.textMuted};
  }
`;

export const StepBody = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACE[12]};
  /* Off-scale on purpose: centres the first line of copy on the 24px step number. */
  padding-top: 2px;
`;

export const StepText = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  color: ${COLORS.text};
`;

/** "Open Search Console": an <a> dressed as the outline Button (same pill, 48px height, outline surface and the
 *  shared hover wash) because it navigates to Google in a new tab rather than acting on the page. */
export const OutlineLink = styled.a`
  display: inline-flex;
  align-items: center;
  /* Off-scale on purpose: the Button control's own gap and side padding, so the link and the buttons match. */
  gap: 10px;
  box-sizing: border-box;
  height: 48px;
  padding: 0 18px;
  border-radius: 99px;
  border: 1px solid ${COLORS.ctaBorder};
  background: ${COLORS.ctaOutlineBg};
  color: ${COLORS.ctaText};
  font-size: 16px;
  text-decoration: none;
  cursor: pointer;
  ${hoverWash}

  &:hover,
  &:focus-visible {
    color: ${COLORS.ctaText};
  }
`;

export const Form = styled.form`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const FormRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${SPACE[12]};

  > :first-child {
    flex: 1 1 auto;
    min-width: 0;
  }

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/**
 * The Add button and, while the record is being written, a spinner over it. The label stays in the layout, only
 * see-through (not visibility: hidden, which would also take the button's accessible name away), so the button keeps
 * its width and its name; and it keeps its full colour instead of its dimmed disabled look: it is busy, not unavailable.
 */
export const SubmitSlot = styled.div<{ $busy: boolean }>`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;

  ${(p) =>
    p.$busy &&
    css`
      > button:disabled {
        opacity: 1;
        cursor: progress;
      }

      > button span {
        color: transparent;
      }
    `}

  ${MOBILE} {
    display: flex;

    > button {
      width: 100%;
    }
  }
`;

/** The area's ring spinner, in the button's ink, centred on the button. */
export const SubmitSpinner = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 18px;
  height: 18px;
  /* Half the size back, so the ring's centre sits on the button's centre. */
  margin: -9px 0 0 -9px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid ${COLORS.ctaSolidText};
  border-top-color: transparent;
  pointer-events: none;
  animation: ${spin} ${ANIM.SPIN_MS}ms linear infinite;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** Always rendered so screen readers announce a message that appears in it. */
export const NoticeSlot = styled.div`
  display: block;
`;

export const ErrorText = styled(Text)`
  display: block;
  font-size: 13px;
  line-height: 18px;
  color: ${COLORS.danger};
`;

export const MutedNote = styled(Text)`
  display: block;
  font-size: 13px;
  font-weight: 300;
  line-height: 18px;
  color: ${COLORS.textMuted};
`;

export const RecordsHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[12]};
`;

export const RecordsTitle = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.textStrong};
`;

export const RecordList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const RecordRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[12]};
  min-width: 0;
`;

export const RecordValue = styled(Text)`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12.5px;
  color: ${COLORS.featureName};
`;
